// Account and Team - signed-in profile and account identity, other members, support.

import { ROUTES } from 'src/router/const'

import {
  BULLET_VARIANTS,
  FAQ_BLOCK_TYPES,
  FAQ_SECTION_ICON_TONES,
} from 'src/services/consts/faq/content.const'

/** @type {import('src/services/helpers/faq/content.helper').FaqTopic} */
export const ACCOUNT_AND_TEAM_TOPIC = {
  id: 'account-and-team',
  title: 'Account and Team',
  icon: 'users',
  color: 'secondary',
  fill: 'accent-light',
  description:
    'Update your profile and account identity, choose which scan emails reach your inbox, decide who else can access the account, and send a support request.',
  sections: [
    {
      title: 'Account details',
      items: [
        { articleId: 'finish-account-setup', icon: 'user' },
        { articleId: 'leave-account', icon: 'logout' },
        { articleId: 'delete-account', icon: 'trash' },
      ],
    },
    {
      title: 'Scan emails',
      items: [{ articleId: 'update-email-preferences', icon: 'mail' }],
    },
    {
      title: 'Members',
      items: [{ articleId: 'manage-account-members', icon: 'users' }],
    },
    {
      title: 'Get help',
      items: [{ articleId: 'contact-support', icon: 'mail' }],
    },
  ],
}

/** @type {Record<string, import('src/services/helpers/faq/content.helper').FaqArticle>} */
export const ACCOUNT_AND_TEAM_ARTICLES = {
  'finish-account-setup': {
    id: 'finish-account-setup',
    title: 'Update your account details',
    description:
      'Change your first and last name, or update Account Name, Company Name, and Country as an admin - email, your own role, password, and 2FA stay outside these edits.',
    keywords: [
      'First Name',
      'Last Name',
      'Account Name',
      'Company Name',
      'Country',
      'change email',
      'Verified',
      'Not verified',
      '2FA',
      'two-factor',
      'password',
      'Save Changes',
      'unsaved changes',
      'own role',
    ],
    lastUpdated: '2026-09-08',
    content: [
      {
        id: 'fas-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'You can change your own **First Name** and **Last Name** after account setup. An **admin** can also update the account identity: **Account Name**, **Company Name**, and **Country**.',
      },
      {
        id: 'fas-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Members can see that identity but cannot edit it. Cookie Armor does not change your email, your own role, your password, or two-factor authentication (2FA).',
      },
      {
        id: 'fas-identity',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Your profile is not the account identity',
        icon: 'user',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'fas-identity-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: '**First Name** and **Last Name** are your profile. The header and Overview greeting use that pair - not **Account Name**.',
          },
          {
            id: 'fas-identity-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Anyone signed in can edit their own names. Empty values cannot be saved.',
          },
          {
            id: 'fas-identity-account-group',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: '**Account Name**, **Company Name**, and **Country** form the account identity shared by its banners and members. Only an **admin** can edit this group.',
          },
          {
            id: 'fas-identity-account-grid',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.GRID,
            items: [
              {
                id: 'fas-identity-account-name',
                icon: 'badge-check',
                title: 'Account Name',
                text: 'Required and must be unique. If another organisation already uses it, pick a different one.',
              },
              {
                id: 'fas-identity-account-company',
                icon: 'building',
                title: 'Company Name',
                text: 'Can be left blank when none is saved, but an existing value cannot be cleared.',
              },
              {
                id: 'fas-identity-account-country',
                icon: 'globe',
                title: 'Country',
                text: 'Optional. Choose from Cookie Armor’s country list.',
              },
            ],
          },
          {
            id: 'fas-identity-account-invited',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'If you were invited, the account identity already existed.',
          },
        ],
      },
      {
        id: 'fas-immutable',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What stays fixed in Cookie Armor',
        icon: 'lock',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'fas-immutable-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            title: 'Cannot change here',
            items: [
              {
                id: 'fas-immutable-email',
                text: 'Your **Email** is read-only. **Verified** means Auth0 has confirmed that address. **Not verified** means it has not. Cookie Armor has no control to change the address or the badge.',
              },
              {
                id: 'fas-immutable-role',
                text: "Your own **Role** is read-only. You cannot promote or demote yourself. An admin changes someone else's role from [[Manage account members|manage-account-members]].",
              },
              {
                id: 'fas-immutable-auth0',
                text: 'Password and 2FA stay on Auth0 Universal Login - Cookie Armor has no signed-in control for either.',
              },
            ],
          },
        ],
      },
      {
        id: 'fas-save',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'When a change is saved',
        icon: 'badge-check',
        color: FAQ_SECTION_ICON_TONES.POSITIVE,
        blocks: [
          {
            id: 'fas-save-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Edits are a draft until you save that group. They do not apply as you type, and they are not a Behaviour **Publish changes** draft.',
          },
          {
            id: 'fas-save-steps',
            type: FAQ_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'fas-save-step-edit',
                title: 'Change one group at a time',
                text: '**First Name** and **Last Name** save together. **Account Name**, **Company Name**, and **Country** save together as a separate change. Saving one group does not write the other.',
              },
              {
                id: 'fas-save-step-save',
                title: 'Save when the draft should persist',
                text: '{btn:save-changes} is available only when that group differs from the last saved values and is valid. First and last name and **Account Name** are required. **Country** is optional. **Company Name** may stay blank when none is saved, but a saved Company Name cannot be cleared. Until the group is saved, its last saved values stay in place.',
              },
              {
                id: 'fas-save-step-cancel',
                title: 'Drop a draft without leaving',
                text: '{btn:cancel} puts that group back to the last saved values and does not write anything.',
              },
            ],
          },
        ],
      },
    ],
  },

  'update-email-preferences': {
    id: 'update-email-preferences',
    title: 'Update email preferences',
    jumpTo: { name: ROUTES.SETTINGS_SYSTEM_SETTINGS },
    description:
      'Turn scan emails on or off for your own inbox. Teammates keep their own email preferences.',
    keywords: [
      'email preferences',
      'Email Preferences',
      'scan email',
      'notifications',
      'Allow all notifications',
      'Scan failed',
      'Scan succeeded',
      'Monthly scan report',
    ],
    lastUpdated: '2026-09-22',
    content: [
      {
        id: 'msep-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Email preferences decide which scan emails Cookie Armor sends to you. Each person on the account has their own, so changing yours does not change what a teammate receives.',
      },
      {
        id: 'msep-emails',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What each scan email covers',
        icon: 'mail',
        color: FAQ_SECTION_ICON_TONES.CYAN,
        blocks: [
          {
            id: 'msep-emails-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'There are three scan emails, and each one can be on or off on its own.',
          },
          {
            id: 'msep-emails-grid',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.GRID,
            items: [
              {
                id: 'msep-email-failed',
                icon: 'alert-circle',
                title: 'Scan failed',
                text: 'Sent when a scan of a site does not finish. Leave it on if you want to know a scan needs attention.',
              },
              {
                id: 'msep-email-success',
                icon: 'circle-check',
                title: 'Scan succeeded',
                text: 'Sent when a scan finishes successfully, so you know the run completed without checking scan history.',
              },
              {
                id: 'msep-email-monthly',
                icon: 'calendar-range',
                title: 'Monthly scan report',
                text: 'A once-a-month summary of scan activity, instead of a message for every scan.',
              },
            ],
          },
        ],
      },
      {
        id: 'msep-allow-all',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Turn every scan email on',
        icon: 'sliders-horizontal',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'msep-allow-all-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: '**Allow all notifications** turns all three scan emails on. Turn it off when you want to keep some and stop others.',
          },
        ],
      },
    ],
  },

  'leave-account': {
    id: 'leave-account',
    title: 'Leave an account',
    description:
      'Leaving ends only your membership and signs you out while the account stays for everyone else - the only admin must promote someone else or delete the account, and stays signed in if they try to leave first.',
    keywords: ['leave account', 'last admin', 'sole admin'],
    lastUpdated: '2026-09-29',
    content: [
      {
        id: 'la-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Leaving ends only your membership. The account stays for everyone else.',
      },
      {
        id: 'la-outcome',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What leaving changes',
        icon: 'logout',
        color: FAQ_SECTION_ICON_TONES.NEGATIVE,
        blocks: [
          {
            id: 'la-outcome-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Banners, settings, and the other people on the account stay. You are signed out, and the leave cannot be undone.',
          },
        ],
      },
      {
        id: 'la-only-admin',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'When you are the only admin',
        icon: 'shield-alert',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'la-only-admin-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'If you are the only admin, you cannot leave yet. Trying to leave keeps you signed in.',
          },
          {
            id: 'la-only-admin-choices',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              {
                id: 'la-only-admin-promote',
                text: 'Promote someone else to **admin** from [[Manage account members|manage-account-members]], then leave.',
              },
              {
                id: 'la-only-admin-delete',
                text: '[[Delete an account|delete-account]] instead, so it does not stay for everyone else.',
              },
            ],
          },
        ],
      },
    ],
  },

  'manage-account-members': {
    id: 'manage-account-members',
    title: 'Manage account members',
    description:
      'Admins invite other people by email, change their role, or permanently remove them - removed people can be invited again with Add Member, the last remaining admin cannot be removed, and you cannot act on yourself.',
    keywords: [
      'Add Member',
      'invite teammate',
      'Update role',
      'Remove member',
      'Pending',
      'already invited',
      'already exists',
      'another account',
      'admin only',
      'delete member',
      'resend',
      're-invite',
      'last admin',
    ],
    lastUpdated: '2026-09-29',
    content: [
      {
        id: 'mam-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'This is about **other people** on the account. Cookie Armor ships two roles: **admin** and **member**.',
      },
      {
        id: 'mam-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'There is no owner, billing, or custom role. Your own name, email, and **Account Name** are in [[Update your account details|finish-account-setup]].',
      },
      {
        id: 'mam-video-section',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'See teammates invited',
        icon: 'file-video',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'mam-video-lead',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A short walkthrough of inviting teammates and managing roles.',
          },
          {
            id: 'mam-video',
            type: FAQ_BLOCK_TYPES.VIDEO,
            videoId: '1223430869',
            hash: '526061222c',
          },
        ],
      },
      {
        id: 'mam-gates',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What each role can do to other people',
        icon: 'users',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'mam-gates-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Role gates apply to team actions. They do not hide banners, scans, installation, analytics, or your own profile.',
          },
          {
            id: 'mam-gates-admin',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            title: 'Admin only',
            items: [
              {
                id: 'mam-gate-invite',
                text: 'Invite someone else and choose their role.',
              },
              {
                id: 'mam-gate-role',
                text: "Change someone else's role between **admin** and **member**.",
              },
              {
                id: 'mam-gate-remove',
                text: 'Permanently remove someone else from the account.',
              },
            ],
          },
          {
            id: 'mam-gates-member',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A **member** sees the same **Active** and **Pending** people as an admin. They cannot invite, change a role, or remove anyone.',
          },
          {
            id: 'mam-gates-self',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'info',
            label: 'No self-actions',
            text: 'Your own row has no actions. You cannot change your role or remove yourself. Your role stays whatever an admin last set. To remove yourself, [[Leave an account|leave-account]]. To remove the account for everyone, [[Delete an account|delete-account]].',
          },
        ],
      },
      {
        id: 'mam-invite',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Invite identity is the email',
        icon: 'mail',
        color: FAQ_SECTION_ICON_TONES.INFO,
        blocks: [
          {
            id: 'mam-invite-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'An admin invites with an email and a role. The default role is **member**.',
          },
          {
            id: 'mam-invite-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Cookie Armor sends the invitation to that address. Delivery is best-effort: a successful add does not prove the email arrived. A **Pending** invite can be resent while it is still waiting.',
          },
          {
            id: 'mam-invite-p3',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'They must join with **that same address** - a different email is a different identity.',
          },
          {
            id: 'mam-invite-states',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              {
                id: 'mam-invite-pending',
                text: '**Pending** - invited, no session on this account yet. They stay pending until they join with the invited address. An admin can still change their role or remove them.',
              },
              {
                id: 'mam-invite-active',
                text: '**Active** - they have joined and can use the account at their current role.',
              },
            ],
          },
          {
            id: 'mam-invite-fail-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              {
                id: 'mam-invite-fail-twice',
                text: 'The same email cannot be invited twice. A live invite already waiting stays pending - do not treat that as a missing person.',
              },
              {
                id: 'mam-invite-fail-member',
                text: 'An address already on this account is already a member.',
              },
              {
                id: 'mam-invite-fail-org',
                text: 'An address that belongs to a different organisation cannot join this one.',
              },
            ],
          },
        ],
      },
      {
        id: 'mam-remove',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Remove member is permanent',
        icon: 'user-x',
        color: FAQ_SECTION_ICON_TONES.NEGATIVE,
        blocks: [
          {
            id: 'mam-remove-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'When an admin chooses **Remove member**, that person loses access and is gone from the account. They do not stay on the team.',
          },
          {
            id: 'mam-remove-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'To bring someone back, invite the same email again with **Add Member**. That starts a new invitation; it does not restore the old membership automatically.',
          },
          {
            id: 'mam-remove-p3',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The last remaining admin on the account cannot be removed.',
          },
          {
            id: 'mam-remove-p4',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Removing someone else does not delete the account. [[Delete an account|delete-account]]',
          },
        ],
      },
    ],
  },

  'delete-account': {
    id: 'delete-account',
    title: 'Delete an account',
    description:
      'Only an admin can delete an account. Deleting removes it for everyone, not only you. Every member loses access, you are signed out, and it cannot be undone.',
    keywords: ['delete account', 'permanently delete', 'remove account', 'close account'],
    lastUpdated: '2026-09-29',
    content: [
      {
        id: 'da-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Only an admin can delete the account. Deleting removes it for everyone. It is not the same as leaving, and it is not the same as removing one person.',
      },
      {
        id: 'da-outcome',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What deleting changes',
        icon: 'trash',
        color: FAQ_SECTION_ICON_TONES.NEGATIVE,
        blocks: [
          {
            id: 'da-outcome-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The account and the data connected to it are deleted. You are signed out, and every member loses access. This cannot be undone.',
          },
        ],
      },
      {
        id: 'da-who',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'When not to delete',
        icon: 'shield-alert',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'da-who-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'If the account should stay, use one of these instead.',
          },
          {
            id: 'da-who-choices',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              {
                id: 'da-who-leave',
                text: '[[Leave an account|leave-account]] if you only want to stop using it yourself. The account stays for everyone else.',
              },
              {
                id: 'da-who-remove',
                text: '[[Manage account members|manage-account-members]] if you only want one other person gone. The account stays.',
              },
            ],
          },
        ],
      },
    ],
  },

  'contact-support': {
    id: 'contact-support',
    title: 'Contact support',
    description:
      'Send a signed-in support request with the banner ID or site ID, timing, and what went wrong - never passwords, tokens, or visitor personal data.',
    keywords: [
      'Contact Support',
      'ticket',
      'help desk',
      'site ID',
      'banner ID',
      'raw error',
      'failed scan',
      'visitor PII',
    ],
    lastUpdated: '2026-08-30',
    content: [
      {
        id: 'cs-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'When an article does not unblock you, send a request from inside Cookie Armor.',
      },
      {
        id: 'cs-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Support is a signed-in form - topic and message only.',
      },
      {
        id: 'cs-intro-3',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'There is no email field. Cookie Armor already knows who is signed in.',
      },
      {
        id: 'cs-include',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What makes a first reply useful',
        icon: 'send',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'cs-include-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Write the message so support can reproduce the problem without asking for the basics.',
          },
          {
            id: 'cs-include-bullets',
            type: FAQ_BLOCK_TYPES.BULLETS,
            variant: BULLET_VARIANTS.CHECKLIST,
            title: 'Include in the message',
            items: [
              {
                id: 'cs-include-banner',
                text: 'The **banner ID** or **site ID** involved.',
              },
              {
                id: 'cs-include-when',
                text: 'When it happened, including the timezone - not only a local clock time.',
              },
              {
                id: 'cs-include-action',
                text: 'The exact action you took, what you expected, and what actually happened.',
              },
              {
                id: 'cs-include-browser',
                text: 'The browser you used.',
              },
              {
                id: 'cs-include-scan-error',
                text: 'For a scan that did not finish, the **raw scan error** as it appeared. Diagnose the error first in [[Fix a failed scan|fix-a-failed-scan]]. A failed-scan path can start a request with that error already in the message; you can still edit it.',
              },
            ],
          },
        ],
      },
      {
        id: 'cs-omit',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What to leave out',
        icon: 'circle-minus',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'cs-omit-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Do not include passwords, Auth0 or API tokens, or personal data about visitors. Support does not need those to act, and they should not sit in a ticket.',
          },
          {
            id: 'cs-omit-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'wrench',
            label: 'Try these first',
            text: 'Script, blocking, and install issues: [[Troubleshoot the script|developer-troubleshooting]]. A scan that did not finish: [[Fix a failed scan|fix-a-failed-scan]].',
          },
        ],
      },
    ],
  },
}
